import { useLingui } from '@lingui/react/macro';

import { UserAvatar } from '@/components/UserAvatar/UserAvatar';
import {
  FilterCheckboxItem,
  FilterItemNoResults,
  FilterSearch,
  FiltersList,
  FiltersSubMenu,
} from '@/design_system/Filters/FiltersDrawer/FiltersDrawer';
import IconUsers from '@/icons/Users.svg';
import { useInfiniteUsers } from '@/models/user';
import useDebouncedState from '@/utils/useDebouncedState';

import { useFilters } from '../FiltersState';

const MINIMUM_USERS_FOR_SEARCH = 5;
const USERS_PER_PAGE = 20;

export const FiltersCollaborator = () => {
  const { t } = useLingui();

  const {
    collaborators: { collaborators, setCollaborators },
  } = useFilters();

  const [search, debouncedSearch, setSearch] = useDebouncedState<string>('', 300);

  // The first hook is to get the total count of users
  // React-query will only fetch once, even with two hooks (as long as the params are the same)
  const { data: initialData } = useInfiniteUsers({
    limit: USERS_PER_PAGE,
  });
  const { data, fetchNextPage, isLoading } = useInfiniteUsers({
    limit: USERS_PER_PAGE,
    search: debouncedSearch || undefined,
  });

  const users = data?.pages.flatMap((page) => page.users) ?? [];
  const count = initialData?.pages[0]?.meta.count || 0;

  const collaboratorsIds = collaborators.map(({ id }) => id);

  const options =
    users.length > 0 ? (
      users.map((user) => (
        <FilterCheckboxItem
          key={user.id}
          id={user.id}
          label={user.name}
          avatar={<UserAvatar user={user} size="x-small" />}
        />
      ))
    ) : (
      <FilterItemNoResults />
    );

  const toggleCollaborator = (userId: string) => {
    const user = users.find((user) => user.id === userId);

    if (user) {
      if (collaboratorsIds.includes(userId)) {
        setCollaborators(collaborators.filter((c) => c.id !== userId));
      } else {
        setCollaborators([...collaborators, { id: user.id, name: user.name }]);
      }
    }
  };

  const optionsWithSearch = (
    <FilterSearch
      ariaLabel={t({ id: 'requests.filters.collaborator.label', message: 'Collaborator' })}
      inputProps={{
        ariaLabel: t({
          id: 'requests.filters.collaborator.search.label',
          message: 'Search collaborators',
        }),
        placeholder: t({
          id: 'requests.filters.collaborator.search.label',
          message: 'Search collaborators',
        }),
        value: search,
        onChange: setSearch,
      }}
      onLoadMore={fetchNextPage}
      isLoading={users.length === 0 && isLoading}
      isLoadingMore={isLoading}
      values={collaboratorsIds}
      onChange={toggleCollaborator}
    >
      {options}
    </FilterSearch>
  );

  const optionsWithoutSearch = (
    <FiltersList
      ariaLabel={t({ id: 'requests.filters.collaborator.label', message: 'Collaborator' })}
      values={collaboratorsIds}
      onChange={toggleCollaborator}
    >
      {options}
    </FiltersList>
  );

  return (
    <FiltersSubMenu
      id="collaborator"
      icon={<IconUsers />}
      label={t({ id: 'requests.filters.collaborator.label', message: 'Collaborator' })}
      count={collaborators.length}
    >
      {count < MINIMUM_USERS_FOR_SEARCH ? optionsWithoutSearch : optionsWithSearch}
    </FiltersSubMenu>
  );
};
